import { GameStateEngine } from '@/core/game-state-engine'
import type { ExpAdmissionCheckpoint } from '@/core/exp-admission'
import { HudLocator } from '@/core/hud-locator'
import { ReadingValidator } from '@/core/reading-validator'
import { metricRecognitionEnabled, recognitionSchedulerIntervalMs } from '@/core/recognition-settings'
import { autoLocateMetricKeys, metricKeys, type CaptureProfile, type GameEvent, type GameStateSnapshot, type MetricReading, type RawReading, type RecognitionMetricKey, type ValidatedObservation } from '@/domain/types'
import type { CompletedExpReading, CompletedResourceReading, GamePreset, HudLocation, PresetRecognitionContext, PresetRecognizer } from '@/presets/types'
import { VideoFrameSource } from './video-frame-source'
import { FrameScheduler, type FrameSchedulerHealth } from './frame-scheduler'
import { limitedProcessingInterval } from '@/core/performance-settings'
import { performanceDiagnostics } from './performance-diagnostics'
import { classifyOcrError } from '@/core/ocr-recovery'
import { type CalibrationInitializationOptions, type CalibrationProgress, applyCalibration, calibrationRegionsChanged, LayoutConfirmation } from '@/core/layout-calibration'
import { drawSourceToImageData } from '@/core/image'
import { captureVideoBitmap } from '@/core/video-bitmap'
import { calibrationDelay, canConfirmStaticLayout, healthyStaticCapture } from '@/core/calibration-sampling'
import type { LayoutSearchDiagnostic } from '@/presets/types'
import { BackgroundLayoutSearch } from './background-layout-search'
import type { OcrEngine } from './ocr-engine'

const MESOS_SEARCH_BUDGET_MS = 1_000
const MESOS_RETRY_TIMEOUT_MS = 2_000

export interface CalibrationPersistence {
  save(next: CaptureProfile, previous: CaptureProfile, isCurrent?: () => boolean): Promise<CaptureProfile>
  loadSize(width: number, height: number): Promise<CaptureProfile>
}

export type SessionSamplingStage = 'waiting-frame' | 'recognizing' | 'confirming'
export type SamplingStageListener = (stage: SessionSamplingStage) => void

export interface PipelineResult {
  readonly captureGeneration: number
  readonly raw: RawReading
  readonly observation: ValidatedObservation
  readonly state: GameStateSnapshot
  readonly events: readonly GameEvent[]
  readonly hud: HudLocation
}

export function maskDisabledStatisticsReading(
  raw: RawReading,
  recognition: CaptureProfile['recognition'],
): RawReading {
  const disabled = metricKeys.filter((metric) => !metricRecognitionEnabled(recognition, metric))
  if (!disabled.length) return raw
  const result = { ...raw }
  if (raw.sourceObservation) {
    const source = raw.sourceObservation
    result.sourceObservation = { ...source,
      mesosVisibility: disabled.includes('mesos') ? 'hidden' : source.mesosVisibility,
    }
  }
  for (const metric of disabled) {
    const source = raw[metric].source
    Object.assign(result, {
      [metric]: {
        value: null,
        confidence: 0,
        source,
        availability: 'unavailable',
        capturedAt: raw.capturedAt,
      } satisfies MetricReading<never>,
    })
  }
  return result
}

export class AnalysisPipeline {
  private readonly locator = new HudLocator()
  private readonly validator: ReadingValidator
  private readonly stateEngine: GameStateEngine
  private readonly recognizer: PresetRecognizer
  private scheduler: FrameScheduler | null = null
  private sessionId = ''
  private captureGeneration = 0
  private diagnosticMetric: RecognitionMetricKey | null = null
  private source: VideoFrameSource | null = null
  private sourceVideo: HTMLVideoElement | null = null
  private lastProcessedFrameId = 0
  private processing = false
  private lastRaw: { raw: RawReading; hud: HudLocation } | null = null
  private expDeliveryEpoch = 0
  private readonly lastCompletionAt = new Map<'exp' | 'hp' | 'mp', number>()
  private readonly pendingDeliveries = new Map<'exp' | 'hp' | 'mp', {
    result: CompletedExpReading | CompletedResourceReading; current: () => boolean; resolve: () => void
  }>()
  private pendingProfile: CaptureProfile | null = null
  private pendingProcessingLimit: number | null = null
  private recognitionRevision = 0
  private lastObservation: ValidatedObservation | null = null
  private readonly backgroundLayout: BackgroundLayoutSearch
  private initialization: AbortController | null = null
  private sourceRevision = 0
  private unsavedCalibration: { result: LayoutConfirmation; overwrite: boolean; profile: CaptureProfile; metrics: readonly RecognitionMetricKey[]; generation: number; sourceRevision: number } | null = null

  constructor(
    private readonly preset: GamePreset,
    private profile: CaptureProfile,
    private readonly onResult: (result: PipelineResult) => void,
    private readonly onStalled?: (health: FrameSchedulerHealth) => void,
    private processingFpsLimit = 0,
    private readonly captureIntervalMs: () => number = () => 1_000,
    private readonly calibration?: CalibrationPersistence,
    private readonly inspectRankSource?: (frame: HTMLVideoElement, engine: OcrEngine) => void,
    private readonly requestFrameRefresh?: (signal: AbortSignal) => Promise<unknown>,
  ) {
    this.backgroundLayout = new BackgroundLayoutSearch(preset, () => this.samplingIntervalMs())
    this.validator = new ReadingValidator(preset, profile.recognition.minConfidence)
    this.stateEngine = new GameStateEngine(preset)
    this.recognizer = preset.createRecognizer(profile)
    this.recognizer.updateRecognitionSettings?.(profile.recognition, null)
  }

  start(video: HTMLVideoElement, sessionId: string, baseline: GameStateSnapshot | null = null, captureGeneration = 0, checkpoint?: ExpAdmissionCheckpoint, options: { deferSampling?: boolean } = {}): void {
    this.stop()
    this.sessionId = sessionId
    this.captureGeneration = captureGeneration
    this.recognizer.resetContinuity?.()
    this.validator.resetBaseline(baseline)
    if (checkpoint) this.validator.restoreExpCheckpoint(checkpoint)
    this.stateEngine.resetBaseline(baseline)
    this.recognizer.requestMetrics?.(['exp', 'level'])
    if (options.deferSampling) this.ensureSource(video)
    else this.resume(video)
  }

  resume(video: HTMLVideoElement): void {
    if (this.scheduler) return
    const source = this.ensureSource(video)
    this.scheduler = new FrameScheduler(source, this.schedulerInterval(), {
      captureIntervalMs: this.captureIntervalMs,
      onStalled: (health) => this.onStalled?.(health),
      requestFrameRefresh: this.requestFrameRefresh,
    })
    this.scheduler.start((_frame, signal) => this.processNewFrame(video, { signal }))
  }

  rebindVideo(video: HTMLVideoElement): void {
    if (this.sourceVideo === video) { this.pulse(true); return }
    this.invalidateExpDelivery()
    this.sourceRevision += 1
    this.scheduler?.stop()
    this.scheduler = null
    this.ensureSource(video, true)
    this.resume(video)
  }

  async settle(video: HTMLVideoElement, requestedMetrics: readonly RecognitionMetricKey[], signal: AbortSignal): Promise<boolean> {
    this.initialization?.abort()
    await this.suspendScheduler()
    if (signal.aborted) return false
    while (!signal.aborted) {
      const sampled = await this.processNewFrame(video, { mode: 'settlement', requestedMetrics, signal }, true)
      if (!sampled) return false
      if (requestedMetrics.every(metric => this.lastObservation?.accepted[metric]
        && this.lastObservation[metric].availability === 'available'
        && this.lastObservation[metric].capturedAt === this.lastObservation.capturedAt
        && this.lastObservation.diagnostics.metrics?.[metric]?.status !== 'confirming')) return true
    }
    return false
  }

  async prepareForSessionStart(signal: AbortSignal, onStage?: SamplingStageListener): Promise<void> {
    this.initialization?.abort()
    onStage?.('recognizing')
    await this.suspendScheduler()
    if (signal.aborted) return
    await this.recognizer.preparePriorityRecognition?.(Number.POSITIVE_INFINITY, signal)
  }

  async sampleForSessionStart(video: HTMLVideoElement, requestedMetrics: readonly RecognitionMetricKey[], signal: AbortSignal, onStage?: SamplingStageListener): Promise<boolean> {
    await this.prepareForSessionStart(signal, onStage)
    if (signal.aborted) return false
    return this.processNewFrame(video, { mode: 'settlement', requestedMetrics, signal }, true, onStage)
  }

  async beginSession(
    video: HTMLVideoElement,
    sessionId: string,
    baseline: GameStateSnapshot | null,
    captureGeneration: number,
    minimumSourceAt: number,
  ): Promise<void> {
    await this.suspendScheduler()
    this.sessionId = sessionId
    this.captureGeneration = captureGeneration
    this.validator.resetBaseline(baseline)
    this.stateEngine.resetBaseline(baseline)
    if (this.recognizer.beginSession) this.recognizer.beginSession(minimumSourceAt)
    else this.recognizer.requestMetrics?.(['exp'])
    if (this.recognizer.requestSessionMapRefresh) this.recognizer.requestSessionMapRefresh()
    else this.recognizer.requestMetrics?.(['mapName'])
    this.resume(video)
  }

  samplingIntervalMs(): number { return Math.max(this.captureIntervalMs(), this.schedulerInterval()) }

  requestExpConfirmation(): void {
    if (this.recognizer.requestExpConfirmation) this.recognizer.requestExpConfirmation()
    else this.recognizer.requestMetrics?.(['level', 'exp'])
  }

  updateProfile(profile: CaptureProfile): void {
    this.invalidateExpDelivery()
    this.backgroundLayout.cancel(true)
    this.recognitionRevision += 1
    const moved = calibrationRegionsChanged(this.profile, profile)
    if (moved.length) this.recognizer.invalidateRegions?.(moved)
    this.pendingProfile = profile
    if (this.processing || this.recognizer.isBusy?.()) return
    this.applyPendingSettings()
  }

  private applyPendingSettings(): void {
    if (this.pendingProcessingLimit !== null) {
      this.processingFpsLimit = this.pendingProcessingLimit
      this.pendingProcessingLimit = null
    }
    const profile = this.pendingProfile
    this.pendingProfile = null
    if (profile) this.applyProfile(profile)
    this.scheduler?.setInterval(this.schedulerInterval())
  }

  private applyProfile(profile: CaptureProfile): void {
    const moved = calibrationRegionsChanged(this.profile, profile)
    if (moved.length) this.recognizer.invalidateRegions?.(moved)
    const changedContinuity = metricKeys.filter((metric) => (
      metricRecognitionEnabled(this.profile.recognition, metric)
      !== metricRecognitionEnabled(profile.recognition, metric)
    ))
    this.profile = profile
    const movedStatistics = moved.filter((metric): metric is typeof metricKeys[number] => metric !== 'jobName')
    if (movedStatistics.length) {
      this.validator.resetMetricContinuity(movedStatistics)
      this.stateEngine.markMetricsDiscontinuous(movedStatistics)
    }
    this.recognitionRevision += 1
    this.validator.setMinimumConfidence(profile.recognition.minConfidence)
    if (changedContinuity.length) {
      this.validator.resetMetricContinuity(changedContinuity)
      this.stateEngine.markMetricsDiscontinuous(changedContinuity)
    }
    this.recognizer.updateRecognitionSettings?.(profile.recognition, this.diagnosticMetric)
    this.scheduler?.setInterval(this.schedulerInterval())
  }

  setDiagnosticMetric(metric: RecognitionMetricKey | null): void {
    if (metric === this.diagnosticMetric) return
    this.invalidateExpDelivery()
    this.backgroundLayout.cancel()
    this.diagnosticMetric = metric
    this.recognitionRevision += 1
    this.recognizer.updateRecognitionSettings?.(this.profile.recognition, metric)
    this.scheduler?.setInterval(this.schedulerInterval())
    this.scheduler?.pulse(true)
  }

  pulse(force = false): boolean {
    return this.scheduler?.pulse(force) ?? false
  }

  performanceState() {
    return {
      processingFpsLimit: this.processingFpsLimit,
      metricRecognitionIntervalsMs: { ...this.profile.recognition.metricRecognitionIntervalsMs },
      changePending: this.pendingProfile !== null || this.pendingProcessingLimit !== null,
    }
  }

  health(): FrameSchedulerHealth | null {
    return this.scheduler?.health() ?? null
  }

  setCaptureGeneration(captureGeneration: number, adaptiveMetricToReset?: RecognitionMetricKey): void {
    if (captureGeneration === this.captureGeneration) return
    this.invalidateExpDelivery()
    this.backgroundLayout.cancel(true)
    if (this.initialization) {
      this.initialization.abort()
      if (this.sourceVideo) this.resume(this.sourceVideo)
    }
    this.captureGeneration = captureGeneration
    this.recognizer.resetContinuity?.(adaptiveMetricToReset)
    this.stateEngine.markMetricsDiscontinuous(['mesos', 'hp', 'mp', 'mapName'])
    this.validator.resetExpContinuity()
  }

  expCheckpoint(): ExpAdmissionCheckpoint { return this.validator.expCheckpoint() }

  resetBaseline(adaptiveMetricToReset?: RecognitionMetricKey, checkpoint?: ExpAdmissionCheckpoint): void {
    this.invalidateExpDelivery()
    this.recognizer.resetContinuity?.(adaptiveMetricToReset)
    this.validator.resetBaseline()
    if (checkpoint) this.validator.restoreExpCheckpoint(checkpoint)
    this.stateEngine.resetBaseline()
    this.recognizer.requestMetrics?.(['exp', 'level'])
  }

  stop(): void {
    this.invalidateExpDelivery()
    this.backgroundLayout.cancel(true)
    this.sourceRevision += 1
    this.unsavedCalibration = null
    this.initialization?.abort()
    this.validator.resetExpContinuity()
    this.scheduler?.stop()
    this.scheduler = null
    this.source?.dispose()
    this.source = null
    this.sourceVideo = null
    this.lastProcessedFrameId = 0
    performanceDiagnostics.setProcessingInterval(null)
  }

  setProcessingFrameRateLimit(limit: number): void {
    this.invalidateExpDelivery()
    this.pendingProcessingLimit = limit
    if (!this.processing && !this.recognizer.isBusy?.()) this.applyPendingSettings()
  }

  private schedulerInterval(): number {
    const interval = limitedProcessingInterval(recognitionSchedulerIntervalMs(this.profile.recognition), this.processingFpsLimit)
    performanceDiagnostics.setProcessingInterval(interval)
    return interval
  }

  dispose(): void {
    this.stop()
    this.recognizer.dispose()
  }

  private async suspendScheduler(): Promise<void> {
    this.invalidateExpDelivery()
    this.backgroundLayout.cancel()
    const scheduler = this.scheduler
    scheduler?.stop()
    this.scheduler = null
    performanceDiagnostics.setProcessingInterval(null)
    try {
      await scheduler?.waitForIdle()
    } catch {
      // A failed scheduled frame must not prevent a priority sample.
    }
  }

  private ensureSource(video: HTMLVideoElement, skipInitialFrame = false): VideoFrameSource {
    if (!this.source || this.sourceVideo !== video) {
      this.source?.dispose()
      this.source = new VideoFrameSource(video, () => Math.max(500, this.captureIntervalMs() * 2, this.schedulerInterval() * 2), { skipInitialFrame })
      this.sourceVideo = video
      this.lastProcessedFrameId = 0
    }
    return this.source
  }

  private async processNewFrame(
    video: HTMLVideoElement,
    options: Pick<PresetRecognitionContext, 'mode' | 'requestedMetrics' | 'signal'>,
    wait = false,
    onStage?: SamplingStageListener,
  ): Promise<boolean> {
    if (this.processing || options.signal?.aborted) return false
    this.processing = true
    try {
      if (this.pendingProfile || this.pendingProcessingLimit !== null || wait) await this.recognizer.waitForIdle?.()
      if (options.signal?.aborted) return false
      this.applyPendingSettings()
      const source = this.ensureSource(video)
      onStage?.('waiting-frame')
      const frame = wait && options.signal
        ? await source.waitAfter(this.lastProcessedFrameId, options.signal, this.requestFrameRefresh
          ? { request: this.requestFrameRefresh, intervalMs: this.captureIntervalMs } : undefined)
        : source.read()
      if (!frame || frame.id <= this.lastProcessedFrameId || options.signal?.aborted) return false
      this.lastProcessedFrameId = frame.id
      onStage?.('recognizing')
      return await this.process(video, frame.sourceCapturedAt ?? frame.capturedAt, options)
    } finally {
      onStage?.('confirming')
      this.processing = false
      this.flushCompletionDelivery()
    }
  }

  private async process(
    video: HTMLVideoElement,
    capturedAt: number,
    options: Pick<PresetRecognitionContext, 'mode' | 'requestedMetrics' | 'signal'> = {},
  ): Promise<boolean> {
    if (!video.videoWidth || !video.videoHeight || options.signal?.aborted) return false
    const captureGeneration = this.captureGeneration
    const sourceRevision = this.sourceRevision
    const expDeliveryEpoch = this.expDeliveryEpoch
    let recognitionRevision = this.recognitionRevision
    const startedAt = performance.now()
    let bitmap: ImageBitmap | undefined
    try {
      bitmap = await captureVideoBitmap(video)
      if (options.signal?.aborted) return false
      if (captureGeneration !== this.captureGeneration || sourceRevision !== this.sourceRevision || options.signal?.aborted) return false
      let hud: HudLocation
      if (this.preset.locateLayout && this.profile.layoutVersion === this.preset.layoutVersion) {
        if (bitmap.width !== this.profile.sourceWidth || bitmap.height !== this.profile.sourceHeight) {
          if (!this.calibration) return false
          const restored = await this.calibration.loadSize(bitmap.width, bitmap.height)
          if (captureGeneration !== this.captureGeneration || options.signal?.aborted) return false
          this.backgroundLayout.cancel(true)
          this.applyProfile(restored)
        }
        if (!options.mode && this.backgroundLayout.awaitingConfirmation) {
          const previous = this.profile
          const confirmed = this.backgroundLayout.confirm(drawSourceToImageData(bitmap, bitmap.width, bitmap.height), previous, capturedAt)
          if (confirmed) {
            const isCurrent = () => confirmed.isCurrent() && captureGeneration === this.captureGeneration
              && previous === this.profile && !this.pendingProfile && !options.signal?.aborted
            try { await this.saveCalibration(confirmed.result, false, confirmed.metrics, isCurrent) }
            catch { if (isCurrent()) confirmed.saveFailed() }
            if (this.pendingProfile || sourceRevision !== this.sourceRevision || captureGeneration !== this.captureGeneration || options.signal?.aborted) return false
          }
        }
        const located = Boolean(this.profile.autoRois?.hp || this.profile.roiOverrides.hp)
        hud = { rect: this.profile.hudRect, confidence: located ? this.profile.autoRois?.hp?.confidence ?? 1 : 0, method: located ? 'detected' : 'fallback' }
        recognitionRevision = this.recognitionRevision
      } else hud = this.locator.locate(bitmap, this.preset, bitmap.width, bitmap.height)
      const completionIsCurrent = () => !!this.scheduler && !options.signal?.aborted && !this.pendingProfile
        && expDeliveryEpoch === this.expDeliveryEpoch && captureGeneration === this.captureGeneration
        && sourceRevision === this.sourceRevision && recognitionRevision === this.recognitionRevision
      const rawResult = await this.recognizer.recognize({
        frame: bitmap,
        profile: this.profile,
        capturedAt,
        hudRect: hud.rect,
        diagnosticMetric: this.diagnosticMetric,
        samplingIntervalMs: Math.max(this.captureIntervalMs(), this.schedulerInterval()),
        ...(!options.mode && !this.initialization && !this.diagnosticMetric && this.inspectRankSource
          ? { onOcrIdle: (engine: OcrEngine) => {
            // The core task can finish after its bitmap has been closed. Read
            // the current video only while this source and scheduled run live.
            if (options.signal?.aborted || captureGeneration !== this.captureGeneration
              || sourceRevision !== this.sourceRevision || recognitionRevision !== this.recognitionRevision
              || this.initialization || this.diagnosticMetric) return
            this.inspectRankSource!(video, engine)
          } } : {}),
        ...(!options.mode && this.preset.id === 'mxdc' && !this.diagnosticMetric ? {
          onExpCompleted: (result: CompletedExpReading) => this.receiveCompletion(result, completionIsCurrent),
          onResourceCompleted: (result: CompletedResourceReading) => this.receiveCompletion(result, completionIsCurrent),
        } : {}),
        ...options,
      })
      if (this.profile.layoutVersion && rawResult.diagnostics.hudConfidence === 0) hud = { ...hud, confidence: 0, method: 'fallback' }
      if (captureGeneration !== this.captureGeneration
        || sourceRevision !== this.sourceRevision
        || recognitionRevision !== this.recognitionRevision
        || options.signal?.aborted) return false
      const raw: RawReading = { ...rawResult, diagnostics: { ...rawResult.diagnostics, hudConfidence: hud.confidence } }
      this.lastRaw = { raw, hud }
      this.publishReading(raw, hud)
      if (this.profile.layoutVersion && !options.mode) {
        const repairMetrics: readonly RecognitionMetricKey[] = this.preset.id === 'gcw'
          ? ['hp', 'mp', 'jobName', 'mesos'] : ['hp', 'mp', 'jobName']
        this.backgroundLayout.requestRepair(repairMetrics.filter(metric =>
          this.profile.autoRois?.[metric] && !(metric === 'mesos' && this.profile.roiOverrides.mesos)
          && raw.diagnostics.metrics?.[metric]?.failureReason === 'layout-unavailable'))
      }
      if (!options.mode && !this.initialization && !this.pendingProfile && !options.signal?.aborted
        && captureGeneration === this.captureGeneration && recognitionRevision === this.recognitionRevision
        && sourceRevision === this.sourceRevision
        && !this.unsavedCalibration && this.backgroundLayout.needsFrame(this.profile)) {
        this.backgroundLayout.start(drawSourceToImageData(bitmap, bitmap.width, bitmap.height), this.profile, capturedAt)
      }
      return true
    } catch (error) {
      if (!options.signal?.aborted && classifyOcrError(error).code !== 'cancelled') {
        performanceDiagnostics.error(classifyOcrError(error).code === 'image-error' ? 'image-error' : 'frame-error')
      }
      throw error
    } finally {
      bitmap?.close()
      performanceDiagnostics.measure('frame', performance.now() - startedAt)
    }
  }

  private publishReading(raw: RawReading, hud: HudLocation): void {
    performanceDiagnostics.setRecognitionContext({ presetId: this.preset.id,
      modelName: this.preset.remoteResources.ocrModels.recognition.name,
      modelVersion: this.preset.remoteResources.ocrModels.recognition.version })
    performanceDiagnostics.observeMesosRecognition(this.profile, raw, this.captureGeneration, this.sourceRevision)
    if (this.preset.id === 'mxdc') {
      const scope = `${this.captureGeneration}:${this.sessionId}`
      performanceDiagnostics.observeExpDelivery(scope, raw)
      performanceDiagnostics.observeResourceDelivery(scope, raw)
    }
    const observation = this.validator.validate(maskDisabledStatisticsReading(raw, this.profile.recognition))
    const expReviews = [
      { sourceAt: observation.expExactCandidate?.sourceAt, admission: observation.expCandidateAdmission },
      { sourceAt: observation.expExactEvidence?.sourceAt, admission: observation.expEvidenceAdmission },
      { sourceAt: observation.expPercentCandidate?.sourceAt, admission: observation.expEstimatedAdmission },
    ].filter(review => review.sourceAt !== undefined && review.admission)
      .sort((left, right) => left.sourceAt! - right.sourceAt!)
    for (const review of expReviews) {
      this.recognizer.observeExpAdmission?.(review.sourceAt!, review.admission!.status === 'accepted')
    }
    for (const metric of [...metricKeys, 'jobName'] as const) {
      if (observation.accepted[metric]) performanceDiagnostics.observeMetric(metric, observation[metric].capturedAt)
    }
    this.lastObservation = observation
    const { state, events } = this.stateEngine.apply(this.sessionId, observation)
    this.onResult({ captureGeneration: this.captureGeneration, raw, observation, state, events, hud })
  }

  /** Recording lifecycle changes invalidate resource completions alongside EXP. */
  invalidateExpDelivery(): void {
    this.expDeliveryEpoch++
    for (const pending of this.pendingDeliveries.values()) pending.resolve()
    this.pendingDeliveries.clear()
    this.lastRaw = null
    this.lastCompletionAt.clear()
  }

  private receiveCompletion(result: CompletedExpReading | CompletedResourceReading, current: () => boolean): Promise<void> | void {
    const metric = 'metric' in result ? result.metric : 'exp'
    const latest = Math.max(this.lastCompletionAt.get(metric) ?? -Infinity,
      this.pendingDeliveries.get(metric)?.result.capturedAt ?? -Infinity)
    if (!current() || result.capturedAt <= latest) return
    return new Promise<void>(resolve => {
      this.pendingDeliveries.get(metric)?.resolve()
      this.pendingDeliveries.set(metric, { result, current, resolve })
      if (!this.processing) this.flushCompletionDelivery()
    })
  }

  private flushCompletionDelivery(): void {
    const deliveries = [...this.pendingDeliveries.values()]
    this.pendingDeliveries.clear()
    try {
      for (const pending of deliveries) {
        const { result, current } = pending, previous = this.lastRaw
        const metric = 'metric' in result ? result.metric : 'exp'
        if (!current() || !previous || result.capturedAt <= (this.lastCompletionAt.get(metric) ?? -Infinity)) continue
        this.lastCompletionAt.set(metric, result.capturedAt)
        // A regular sample may already have delivered this completion while the
        // async task was finishing. Never publish the same source twice here.
        if ((previous.raw.diagnostics.metrics?.[metric]?.completion?.capturedAt ?? -Infinity) >= result.capturedAt) continue
        const diagnostic = result.diagnostic
        const raw: RawReading = { ...previous.raw,
          capturedAt: Math.max(previous.raw.capturedAt, result.capturedAt),
          ...('metric' in result ? { [metric]: result.reading }
            : { exp: result.exp, expExactCandidate: result.expExactCandidate, expExactEvidence: result.expExactEvidence, expPercentCandidate: result.expPercentCandidate }),
          ...(result.sourceId ? { metricSourceIds: { ...previous.raw.metricSourceIds, [metric]: result.sourceId } } : {}),
          diagnostics: { ...previous.raw.diagnostics, metrics: { ...previous.raw.diagnostics.metrics,
            [metric]: diagnostic && { ...diagnostic, completion: diagnostic.completion && {
              ...diagnostic.completion, deliveredAt: Date.now(),
            } },
          } },
        }
        this.lastRaw = { ...previous, raw }
        this.publishReading(raw, previous.hud)
      }
    } finally { for (const pending of deliveries) pending.resolve() }
  }

  private async saveCalibration(result: LayoutConfirmation, overwrite: boolean, metrics: readonly RecognitionMetricKey[] = autoLocateMetricKeys, isCurrent?: () => boolean): Promise<void> {
    if (isCurrent && !isCurrent()) throw new Error('Calibration cancelled')
    if (this.pendingProfile) throw new Error('Calibration cancelled: profile changed')
    const sourceRevision = this.sourceRevision
    const previous = this.profile
    const next = applyCalibration(previous, result, overwrite)
    // A background confirmation can leave every region unchanged. Its save
    // timestamp must not invalidate an otherwise current async OCR result.
    if (this.preset.id === 'mxdc' && !overwrite && !calibrationRegionsChanged(previous, next).length
      && JSON.stringify(previous.hudRect) === JSON.stringify(next.hudRect)) return
    if (overwrite) this.unsavedCalibration = { result, overwrite, profile: previous, metrics: [...metrics], generation: this.captureGeneration, sourceRevision }
    const saved = this.calibration ? await this.calibration.save(next, previous, isCurrent) : next
    if (isCurrent && !isCurrent()) throw new Error('Calibration cancelled')
    this.unsavedCalibration = null
    if (this.sourceRevision !== sourceRevision || this.profile !== previous || this.pendingProfile) throw new Error('Calibration cancelled: profile changed')
    this.applyProfile(saved)
  }

  /** One explicit operation. Replaces only confirmed regions and never raises capture FPS. */
  async initializeLayout(video: HTMLVideoElement, options: CalibrationInitializationOptions = {}): Promise<RecognitionMetricKey[]> {
    if (!this.preset.locateLayout || this.initialization) throw new Error('Calibration is unavailable')
    this.backgroundLayout.cancel(true)
    const metrics = autoLocateMetricKeys.filter(metric => !options.metrics || options.metrics.includes(metric))
    if (!metrics.length) throw new Error('Calibration is unavailable')
    const mesosOnly = metrics.length === 1 && metrics[0] === 'mesos'
    const startedAt = Date.now()
    let deadlineAt = mesosOnly ? startedAt + MESOS_RETRY_TIMEOUT_MS : undefined
    const controller = new AbortController()
    this.initialization = controller
    let saving = false
    const cancel = () => { if (!saving) controller.abort('user-cancel') }
    options.signal?.addEventListener('abort', cancel, { once: true })
    if (options.signal?.aborted) cancel()
    const generation = this.captureGeneration
    const sourceRevision = this.sourceRevision
    let timeout = window.setTimeout(() => controller.abort('timeout'), mesosOnly ? MESOS_RETRY_TIMEOUT_MS : 10_000)
    let diagnostics: LayoutSearchDiagnostic | undefined
    const searchTimings = { hudSearchMs: 0, mapSearchMs: 0, mesosSearchMs: 0 }
    const observeSearch = (result: LayoutSearchDiagnostic | undefined) => {
      for (const key of ['hudSearchMs', 'mapSearchMs', 'mesosSearchMs'] as const) searchTimings[key] += result?.[key] ?? 0
    }
    const failed = new Set<RecognitionMetricKey>()
    let confirmation: CalibrationProgress['confirmation']
    const captureSize = { width: video.videoWidth, height: video.videoHeight }
    const collected = new LayoutConfirmation()
    const report = (stage: CalibrationProgress['stage'], located: RecognitionMetricKey[] = [], confirming: RecognitionMetricKey[] = [], saved: RecognitionMetricKey[] = []) => {
      if (sourceRevision === this.sourceRevision && generation === this.captureGeneration && (!controller.signal.aborted || controller.signal.reason === 'timeout')) options.onProgress?.({ stage, located, confirming, saved, metrics, failed: [...failed],
        diagnostics: { ...diagnostics, candidates: diagnostics?.candidates ?? 0, ...searchTimings, durationMs: Date.now() - startedAt,
          ...(controller.signal.reason === 'timeout' ? { reason: 'timeout' as const } : {}) },
        confirmation, captureSize })
    }
    try {
      report('preparing')
      const ready = await this.waitForCalibrationIdle(controller.signal)
      if (controller.signal.aborted && controller.signal.reason !== 'timeout') throw new Error('Calibration cancelled')
      if (!ready) {
        diagnostics = { reason: 'timeout', durationMs: mesosOnly ? MESOS_RETRY_TIMEOUT_MS : 10_000, candidates: 0 }
        metrics.forEach(metric => failed.add(metric))
        report('ended'); return []
      }
      if (!mesosOnly) {
        window.clearTimeout(timeout)
        deadlineAt = Date.now() + 15_000
        timeout = window.setTimeout(() => controller.abort('timeout'), 15_000)
      }
      if (video.videoWidth !== this.profile.sourceWidth || video.videoHeight !== this.profile.sourceHeight) throw new Error('Capture dimensions changed')
      if (controller.signal.aborted || sourceRevision !== this.sourceRevision || generation !== this.captureGeneration) throw new Error('Calibration cancelled')
      if (options.action === 'retry-save') {
        const pending = this.unsavedCalibration
        if (!pending?.overwrite || pending.profile !== this.profile || this.pendingProfile
          || pending.generation !== generation || pending.sourceRevision !== sourceRevision
          || pending.metrics.length !== metrics.length || !metrics.every(metric => pending.metrics.includes(metric))) {
          throw new Error('Saved calibration attempt is no longer available')
        }
        const retry = pending.result
        if (this.preset.validateLayout) {
          const image = drawSourceToImageData(video, video.videoWidth, video.videoHeight)
          options.onFrame?.(image)
          retry.verify(this.preset.validateLayout(image, { hud: retry.hud, regions: retry.confirmed }))
        }
        if (deadlineAt !== undefined && Date.now() >= deadlineAt) { report('ended'); return [] }
        const found = metrics.filter(metric => retry.confirmed[metric])
        if (found.length) {
          saving = true
          window.clearTimeout(timeout)
          report('saving', found)
          await this.saveCalibration(retry, true, metrics, options.isCurrent)
          report('ended', found, [], found)
          return found
        }
        this.unsavedCalibration = null
        metrics.forEach(metric => failed.add(metric))
        report('ended')
        return []
      }
      // A new search must never implicitly submit the previous attempt's failed save.
      this.unsavedCalibration = null
      this.applyPendingSettings()
      const source = this.ensureSource(video)
      let frameId = this.lastProcessedFrameId
      const operationProfile = this.profile
      report('locating')
      const startedWithLiveTrack = healthyStaticCapture(video)
      let previousImage: ImageData | undefined
      let previousFrameId = -1
      let previousSampleAt = -Infinity
      let lastDetection: Awaited<ReturnType<NonNullable<GamePreset['locateLayout']>>> = { regions: {} }
      const seen = new Set<RecognitionMetricKey>()
      let mesosCandidate: import('@/domain/types').LocatedMetricRegion | undefined
      let mesosBudgetMs = MESOS_SEARCH_BUDGET_MS
      let mesosReason: import('@/presets/types').LayoutFailureReason | undefined
      while (!controller.signal.aborted) {
        const staticAllowed = healthyStaticCapture(video)
        if (startedWithLiveTrack && !staticAllowed) {
          diagnostics = { ...diagnostics, reason: 'capture-interrupted', durationMs: diagnostics?.durationMs ?? 0, candidates: 0 }
          report('ended')
          throw new Error('Capture interrupted')
        }
        if (staticAllowed && previousImage) await calibrationDelay(Math.max(0, 500 - (Date.now() - previousSampleAt)), controller.signal)
        const sample = staticAllowed ? source.read() : await source.waitAfter(frameId, controller.signal, this.requestFrameRefresh
          ? { request: this.requestFrameRefresh, intervalMs: this.captureIntervalMs } : undefined)
        if (controller.signal.aborted || !staticAllowed && !sample) break
        if (video.videoWidth !== this.profile.sourceWidth || video.videoHeight !== this.profile.sourceHeight) throw new Error('Capture dimensions changed')
        const sampledAt = Date.now()
        const image = drawSourceToImageData(video, video.videoWidth, video.videoHeight)
        options.onFrame?.(image)
        if (controller.signal.aborted || sourceRevision !== this.sourceRevision || generation !== this.captureGeneration) break
        let detection: typeof lastDetection
        const searching = metrics.filter(metric => !failed.has(metric))
        try {
          const verified = this.preset.validateLayout && searching.some(metric => lastDetection.regions[metric])
            ? this.preset.validateLayout(image, lastDetection) : undefined
          // Confirm known anchors on fresh pixels; only invalid candidates need another global search.
          if (verified && searching.every(metric => !lastDetection.regions[metric] || verified.regions[metric])) detection = verified
          else {
            detection = await this.preset.locateLayout(image, searching, {
              signal: controller.signal, deadlineAt, mesosBudgetMs,
              hint: { hud: operationProfile.hudRect, regions: operationProfile.autoRois ?? {} }, mesosCandidate,
            })
            observeSearch(detection.diagnostics)
            mesosBudgetMs = Math.max(0, mesosBudgetMs - (detection.diagnostics?.mesosSearchMs ?? 0))
          }
        } catch (error) {
          if (deadlineAt !== undefined && Date.now() >= deadlineAt) controller.abort('timeout')
          if (controller.signal.aborted) break
          throw error
        }
        if (deadlineAt !== undefined && Date.now() >= deadlineAt) { controller.abort('timeout'); break }
        if (this.profile !== operationProfile || this.pendingProfile || sourceRevision !== this.sourceRevision || generation !== this.captureGeneration || controller.signal.aborted) break
        diagnostics = detection.diagnostics
        if (metrics.includes('mesos') && !failed.has('mesos')) {
          if (!detection.regions.mesos) {
            failed.add('mesos')
            mesosReason = diagnostics?.reasons?.mesos ?? (mesosCandidate ? 'unconfirmed' : 'not-found')
          } else mesosCandidate = detection.regions.mesos
        }
        if (mesosReason) diagnostics = { ...diagnostics, durationMs: diagnostics?.durationMs ?? 0, candidates: diagnostics?.candidates ?? 0, reasons: { ...diagnostics?.reasons, mesos: mesosReason } }
        for (const metric of metrics) if (detection.regions[metric]) seen.add(metric)
        const distinct = sample && sample.id !== previousFrameId
        const staticVerified = Boolean(previousImage) && canConfirmStaticLayout(
          sampledAt, previousSampleAt, video, detection.diagnostics?.anchors ?? detection.diagnostics?.candidates ?? 0,
        )
        const hadPreviousSample = Boolean(previousImage)
        // The independent sample clock is confined to calibration, never source-frame/statistics identity.
        if (!previousImage || distinct || staticVerified) {
          collected.observe(detection, metrics, staticAllowed ? sampledAt : sample!.capturedAt)
          confirmation = staticVerified && !distinct ? 'static-structure' : 'source-frames'
        }
        previousImage = image
        previousFrameId = sample?.id ?? previousFrameId
        previousSampleAt = sampledAt
        frameId = sample?.id ?? frameId
        lastDetection = detection
        report('locating', metrics.filter(metric => collected.confirmed[metric]), metrics.filter(metric => detection.regions[metric] && !collected.confirmed[metric]))
        if (metrics.every(metric => collected.confirmed[metric] || failed.has(metric))) break
        const stillConfirming = metrics.some(metric => detection.regions[metric] && !collected.confirmed[metric] && !(metric === 'mesos' && hadPreviousSample))
        if (!stillConfirming && hadPreviousSample && (distinct || staticVerified)) break
      }
      if (startedWithLiveTrack && !healthyStaticCapture(video)) {
        diagnostics = { ...diagnostics, reason: 'capture-interrupted', durationMs: diagnostics?.durationMs ?? 0, candidates: 0 }
        report('ended')
        throw new Error('Capture interrupted')
      }
      collected.verify(lastDetection)
      if (this.profile !== operationProfile || this.pendingProfile || sourceRevision !== this.sourceRevision || generation !== this.captureGeneration || controller.signal.aborted && controller.signal.reason !== 'timeout') throw new Error('Calibration cancelled')
      if (mesosOnly && controller.signal.reason === 'timeout') delete collected.confirmed.mesos
      if (metrics.some(metric => collected.confirmed[metric])) {
        const verifyController = new AbortController()
        const abortVerify = () => verifyController.abort()
        options.signal?.addEventListener('abort', abortVerify, { once: true })
        const verifyTimer = window.setTimeout(abortVerify, Math.min(3_000, Math.max(0, (deadlineAt ?? Infinity) - Date.now())))
        try {
          if (options.signal?.aborted || sourceRevision !== this.sourceRevision) throw new Error('Calibration cancelled')
          const image = drawSourceToImageData(video, video.videoWidth, video.videoHeight)
          options.onFrame?.(image)
          const verified = this.preset.validateLayout
            ? this.preset.validateLayout(image, { hud: collected.hud, regions: collected.confirmed, supportRegions: lastDetection.supportRegions })
            : await this.preset.locateLayout(image, metrics, { signal: verifyController.signal, deadlineAt, mesosBudgetMs, mesosCandidate })
          if (!this.preset.validateLayout) observeSearch(verified.diagnostics)
          collected.verify(verified)
        } catch (error) {
          if (mesosOnly && deadlineAt !== undefined && Date.now() >= deadlineAt) delete collected.confirmed.mesos
          else throw error
        } finally {
          window.clearTimeout(verifyTimer)
          options.signal?.removeEventListener('abort', abortVerify)
        }
      }
      if (this.profile !== operationProfile || this.pendingProfile || sourceRevision !== this.sourceRevision || generation !== this.captureGeneration || options.signal?.aborted) throw new Error('Calibration cancelled')
      if (mesosOnly && deadlineAt !== undefined && Date.now() >= deadlineAt) delete collected.confirmed.mesos
      const completed = metrics.filter(metric => collected.confirmed[metric])
      const reasons = { ...diagnostics?.reasons }
      for (const metric of metrics) {
        if (!completed.includes(metric)) failed.add(metric)
        if (completed.includes(metric) || reasons[metric] === 'multiple-scenes' || reasons[metric] === 'capture-interrupted') continue
        if (mesosOnly && deadlineAt !== undefined && Date.now() >= deadlineAt) { reasons[metric] = 'timeout'; continue }
        if (seen.has(metric)) reasons[metric] = 'unconfirmed'
      }
      const nextReason = diagnostics?.reason === 'not-found' && seen.size ? undefined : diagnostics?.reason
      diagnostics = {
        durationMs: diagnostics?.durationMs ?? 0,
        candidates: diagnostics?.candidates ?? 0,
        ...(diagnostics?.anchors !== undefined ? { anchors: diagnostics.anchors } : {}),
        ...(diagnostics?.scaleX !== undefined ? { scaleX: diagnostics.scaleX } : {}),
        ...(diagnostics?.scaleY !== undefined ? { scaleY: diagnostics.scaleY } : {}),
        ...(diagnostics?.execution ? { execution: diagnostics.execution } : {}),
        reasons,
        ...(nextReason ? { reason: nextReason } : {}),
      }
      if (completed.length) {
        saving = true
        window.clearTimeout(timeout)
        report('saving', completed)
        await this.saveCalibration(collected, true, metrics, options.isCurrent)
      }
      report('ended', completed, [], completed)
      return completed
    } finally {
      window.clearTimeout(timeout)
      options.signal?.removeEventListener('abort', cancel)
      this.initialization = null
      this.backgroundLayout.cancel(true)
      if (sourceRevision === this.sourceRevision && generation === this.captureGeneration && (!controller.signal.aborted || controller.signal.reason === 'timeout' || controller.signal.reason === 'user-cancel') && (this.sourceVideo === video || controller.signal.reason === 'user-cancel' && !this.sourceVideo)) this.resume(video)
    }
  }

  private async waitForCalibrationIdle(signal: AbortSignal): Promise<boolean> {
    let onAbort!: () => void
    const aborted = new Promise<false>(resolve => {
      onAbort = () => resolve(false)
      if (signal.aborted) onAbort()
      else signal.addEventListener('abort', onAbort, { once: true })
    })
    const idle = (async () => {
      await this.suspendScheduler()
      if (!signal.aborted) await this.recognizer.waitForIdle?.()
      return !signal.aborted
    })()
    try { return await Promise.race([idle, aborted]) }
    finally { signal.removeEventListener('abort', onAbort) }
  }

}
